import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaCalendar, FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { apiCall } from "../config/postgres";
import { useSettings } from "../hooks/useSettings";

const HomeEventsPreview = () => {
  const { t } = useTranslation();
  const { theme } = useSettings();
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadScholarships = async () => {
      try {
        setLoading(true);
        const data = await apiCall("/api/scholarships");
        const latest = (data || [])
          .sort((a, b) => new Date(a.deadline || a.created_at) - new Date(b.deadline || b.created_at))
          .slice(0, 6);
        setScholarships(latest);
      } catch (error) {
        console.error("Error loading scholarships:", error);
      } finally {
        setLoading(false);
      }
    };

    loadScholarships();
  }, []);

  const backgroundImage = theme?.coverImages?.scholarships || theme?.coverImages?.events;

  return (
    <section id="scholarships" className="relative overflow-hidden py-16 md:py-20">
      {backgroundImage && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.65), rgba(0,0,0,0.75)), url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <div className="relative max-w-6xl mx-auto px-6 text-white">
        <div className="flex items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-safir-secondary">
              {t("scholarships.label", "Scholarships")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold">
              {t("scholarships.title", "Latest Scholarships")}
            </h2>
            <p className="text-gray-200 mt-2 max-w-2xl">
              {t(
                "scholarships.previewSubtitle",
                "Discover opportunities to support your education and future."
              )}
            </p>
          </div>
          <a
            href="/scholarships"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/15 border border-white/20 backdrop-blur hover:bg-white/25 transition"
          >
            <span className="font-semibold">{t("scholarships.viewAll", "View all scholarships")}</span>
            <FaArrowRight size={14} />
          </a>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-gray-200">
            <div className="h-10 w-10 rounded-full border-2 border-white/30 border-t-safir-secondary animate-spin" />
            <span>{t("common.loading", "Loading scholarships...")}</span>
          </div>
        ) : scholarships.length === 0 ? (
          <p className="text-gray-200">{t("scholarships.noScholarships", "No scholarships available right now.")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {scholarships.map((scholarship, idx) => {
              const date = scholarship.deadline ? new Date(scholarship.deadline) : null;
              const formatted = date
                ? date.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "";
              return (
                <motion.div
                  key={scholarship.id || idx}
                  className="rounded-2xl bg-white/10 border border-white/15 backdrop-blur p-5 flex flex-col gap-3 hover:-translate-y-1 transition"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                >
                  <div className="flex items-center gap-2 text-sm text-gray-200">
                    <FaCalendar />
                    <span>{formatted || t("scholarships.deadline", "Deadline")}</span>
                  </div>
                  <h3 className="text-xl font-semibold">{scholarship.title}</h3>
                  <p className="text-gray-200 line-clamp-3 text-sm">
                    {scholarship.description || t("scholarships.noDescription", "Details coming soon.")}
                  </p>
                  {scholarship.provider && (
                    <div className="flex items-center gap-2 text-sm text-gray-200">
                      <FaMapMarkerAlt />
                      <span>{scholarship.provider}</span>
                    </div>
                  )}
                  <a
                    href="/scholarships"
                    className="text-sm inline-flex items-center gap-2 text-safir-secondary font-semibold"
                  >
                    {t("scholarships.learnMore", "View scholarship")}
                    <FaArrowRight size={12} />
                  </a>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeEventsPreview;
